import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaLink } from 'react-icons/fa';
import Seo, { absoluteUrl, breadcrumbSchema, faqSchema } from '../../Seo/Seo';
import RelatedProducts from '../../Products/RelatedProducts';
import { blogProductDiscovery } from '../../../data/blogProductDiscovery';
import {
  articleSchema,
  blogPosts,
  enhancePostContent,
  getArticleSection,
  getIsoDate,
  getPostFaqs,
  getPostPath,
  getReadTime,
  getRelatedPosts,
  getTableOfContents,
} from '../blogUtils';
import './BlogPost.scss';

const BlogPost = () => {
  const { blogId } = useParams();
  const blog = blogPosts.find((post) => post.id.toString() === blogId);

  const content = useMemo(() => (blog ? enhancePostContent(blog) : ''), [blog]);
  const tocItems = useMemo(() => getTableOfContents(content), [content]);
  const [activeTocId, setActiveTocId] = useState(tocItems[0]?.id || '');
  const [copiedShare, setCopiedShare] = useState('');
  const shareUrl = useMemo(() => (blog ? absoluteUrl(getPostPath(blog)) : ''), [blog]);
  const shareLinks = useMemo(() => {
    if (!blog || !shareUrl) return null;

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(blog.title);

    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    };
  }, [blog, shareUrl]);

  useEffect(() => {
    setActiveTocId(tocItems[0]?.id || '');
  }, [tocItems]);

  useEffect(() => {
    if (!tocItems.length) return undefined;

    let animationFrameId;

    const updateActiveTocItem = () => {
      const offset = 130;
      const currentItem = tocItems.reduce((current, item) => {
        const heading = document.getElementById(item.id);
        if (!heading) return current;

        return heading.getBoundingClientRect().top <= offset ? item : current;
      }, tocItems[0]);

      setActiveTocId(currentItem?.id || '');
    };

    const handleScroll = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateActiveTocItem);
    };

    updateActiveTocItem();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [tocItems]);

  const handleTocClick = useCallback((event, id) => {
    event.preventDefault();

    const target = document.getElementById(id);
    if (!target) return;

    const headerOffset = 110;
    const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerOffset;
    const nextUrl = `${window.location.pathname}${window.location.search}#${id}`;

    window.history.pushState(null, '', nextUrl);
    setActiveTocId(id);
    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: 'smooth',
    });
  }, []);

  const handleCopyShareLink = useCallback(async (source) => {
    if (!shareUrl) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedShare(source);
      window.setTimeout(() => setCopiedShare(''), 1800);
    } catch (error) {
      setCopiedShare('');
    }
  }, [shareUrl]);

  if (!blog) {
    return (
      <div className="blog-post-container blog-post-not-found mobile-type">
        <h1>Blog post not found.</h1>
        <Link to="/blog">Return to the Klean Leaf blog</Link>
      </div>
    );
  }

  const faqs = getPostFaqs(blog);
  const relatedPosts = getRelatedPosts(blog);
  const productDiscovery = blogProductDiscovery[blog.id];
  const description = blog.metaTags?.description || blog.summary;
  const schemas = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: blog.title, path: getPostPath(blog) },
    ]),
    articleSchema(blog),
    faqSchema(faqs),
  ];

  return (
    <article className="blog-post-container mobile-type">
      <Seo
        title={blog.seoTitle || `${blog.title} | Klean Leaf Blog`}
        description={description}
        path={getPostPath(blog)}
        image={blog.socialImage || blog.imageUrl1}
        imageAlt={blog.title}
        type="article"
        schema={schemas}
      />

      <header className="blog-post-hero">
        <nav className="blog-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/blog">Blog</Link>
        </nav>

        <div className="blog-post-kicker">{getArticleSection(blog)}</div>
        <h1 className="blog-post-title">{blog.title}</h1>
        <p className="blog-post-intro">{blog.summary}</p>

        <div className="blog-post-meta" aria-label="Article details">
          <span>Klean Leaf Team</span>
          <span>Updated {blog.updatedDate || blog.date}</span>
          <span>{getReadTime(blog)}</span>
        </div>

        {shareLinks && (
          <div className="blog-post-share" aria-label="Share this article">
            <span className="blog-post-share-label">Share</span>
            <a
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share this article on LinkedIn"
            >
              <FaLinkedinIn aria-hidden="true" />
              <span className="blog-post-share-text">LinkedIn</span>
            </a>
            <button
              type="button"
              onClick={() => handleCopyShareLink('instagram')}
              aria-label="Copy this article link for Instagram"
            >
              <FaInstagram aria-hidden="true" />
              <span className="blog-post-share-text">
                {copiedShare === 'instagram' ? 'Copied' : 'Instagram'}
              </span>
            </button>
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share this article on Facebook"
            >
              <FaFacebookF aria-hidden="true" />
              <span className="blog-post-share-text">Facebook</span>
            </a>
            <a
              href={shareLinks.x}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share this article on X"
            >
              <span aria-hidden="true">X</span>
              <span className="blog-post-share-text">X</span>
            </a>
            <button
              type="button"
              onClick={() => handleCopyShareLink('copy')}
              aria-label="Copy this article link"
            >
              <FaLink aria-hidden="true" />
              <span className="blog-post-share-text">
                {copiedShare === 'copy' ? 'Copied' : 'Copy Link'}
              </span>
            </button>
          </div>
        )}

        <img
          src={blog.imageUrl1}
          alt={blog.title}
          className="blog-post-featured-image"
          decoding="async"
          fetchpriority="high"
        />
      </header>

      <div className={`blog-post-layout${tocItems.length >= 5 ? '' : ' no-toc'}`}>
        {tocItems.length >= 5 && (
          <aside className="blog-table-of-contents" aria-labelledby="toc-heading">
            <h2 id="toc-heading">In This Article</h2>
            <ol>
              {tocItems.map((item) => (
                <li key={item.id} className={item.level === 3 ? 'toc-nested' : undefined}>
                  <a
                    href={`#${item.id}`}
                    className={activeTocId === item.id ? 'active' : undefined}
                    onClick={(event) => handleTocClick(event, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>
        )}

        <section className="blog-post-content">
          <div
            className="blog-text-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          <section className="blog-post-faq" aria-labelledby="blog-post-faq-heading">
            <span>Buyer questions</span>
            <h2 id="blog-post-faq-heading">Frequently Asked Questions</h2>
            {faqs.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </section>

          {productDiscovery?.productSlugs.length > 0 && (
            <RelatedProducts productSlugs={productDiscovery.productSlugs} />
          )}

          <section className="blog-post-cta" aria-labelledby="blog-post-cta-heading">
            <div>
              <span>{productDiscovery ? 'Explore product options' : 'Private label support'}</span>
              <h2 id="blog-post-cta-heading">{productDiscovery?.title || 'Planning to launch your own laundry sheets brand?'}</h2>
              <p>
                {productDiscovery?.description || 'Klean Leaf helps brands develop private-label laundry detergent sheets with formula, fragrance, packaging, sampling, and production support.'}
              </p>
            </div>
            <div className="blog-post-cta-actions">
              <Link to={productDiscovery?.href || '/contact'} className="blog-post-cta-primary">
                {productDiscovery?.buttonText || 'Start Your Private Label Project'}
              </Link>
              <Link to="/contact" className="blog-post-cta-secondary">Request Samples</Link>
            </div>
          </section>

          <section className="related-posts" aria-labelledby="related-posts-heading">
            <div className="related-posts-heading">
              <span>Keep reading</span>
              <h2 id="related-posts-heading">Related Articles</h2>
            </div>
            <div className="related-posts-grid">
              {relatedPosts.map((post) => (
                <Link key={post.id} to={getPostPath(post)} className="related-post-card">
                  <img
                    src={post.imageUrl1}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                  />
                  <div>
                    <span>{getIsoDate(post.date)} · {getReadTime(post)}</span>
                    <h3>{post.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <meta itemProp="image" content={absoluteUrl(blog.imageUrl1)} />
        </section>
      </div>
    </article>
  );
};

export default BlogPost;
